import { StrictMode, useEffect } from "react";
import * as ReactDOM from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { router } from "./router";
import { adminRouter } from "./routes/admin.routes";
import { createApex25MarketingRouter } from "./routes/apex25-marketing.routes";
import { isApex25MarketingHost } from "./lib/apex25AppUrl";
import { Toaster } from "@/components/ui/toaster";
import { ClerkProvider } from "@clerk/react";
import { initializeSessionAuth } from "./lib/axios";
import { useAppAuth } from "./auth/use-app-auth";
import { useOrganizationBootstrap } from "./auth/use-organization-bootstrap";
import "./styles/main.css";
import { PaymentDialogProvider } from "@/components/billing/PaymentDialog";
import posthog from "posthog-js";
import { AnalyticsIdentity } from "./lib/analytics/AnalyticsIdentity";
import {
  captureFirstTouch,
  getFirstTouch,
  installHandoffDecorator,
} from "./lib/analytics/attribution";
import { firstTouchProperties } from "./lib/analytics/first-touch";
import { initGa4 } from "./lib/analytics/ga4";
import { initPosthog } from "./lib/analytics/posthog";
import { clientEnv } from "./config/env";
import { installChunkRecovery } from "./lib/chunk-recovery";
import { Apex25HeaderAuth } from "./pages/apex25/Apex25HeaderAuth";
import { HeaderAuthSlot } from "./pages/apex25/header-auth-slot";

installChunkRecovery();

// Analytics (PostHog + GA4 "Apex25"). First touch is read before anything renders links, so the
// marketing-site handoff (`ft`) is adopted and removed from the address bar first.
const onMarketingHost = isApex25MarketingHost();
captureFirstTouch();
const posthogReady = initPosthog({ marketingHost: onMarketingHost });
initGa4();
if (posthogReady) {
  const touch = getFirstTouch();
  if (touch) posthog.register(firstTouchProperties(touch));
  if (onMarketingHost) installHandoffDecorator(() => posthog.get_distinct_id());
}

const queryClient = new QueryClient();

// Initialize API authentication; analytics identity is handled by AnalyticsIdentity.
function AppWithAuth() {
  const { getAccessTokenSilently, isAuthenticated } = useAppAuth();

  const isAdminHost =
    typeof window !== "undefined" && window.location.hostname.startsWith("admin.");
  const forceAdmin =
    typeof window !== "undefined" &&
    (clientEnv.forceAdminRouter || new URLSearchParams(window.location.search).has("admin"));
  // Also activate admin router when already on an /admin/* path (e.g. after a page refresh
  // following login, when the ?admin query param is no longer present in the URL).
  const isAdminPath =
    typeof window !== "undefined" && window.location.pathname.startsWith("/admin");
  const useAdmin = isAdminHost || forceAdmin || isAdminPath;
  const organizationReady = useOrganizationBootstrap(!useAdmin);

  useEffect(() => {
    if (isAuthenticated) initializeSessionAuth(() => getAccessTokenSilently());
  }, [isAuthenticated, getAccessTokenSilently]);

  if (!organizationReady) return null;
  return (
    <>
      {!useAdmin && <AnalyticsIdentity />}
      <RouterProvider router={useAdmin ? adminRouter : router} />
    </>
  );
}

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);

if (isApex25MarketingHost()) {
  // tryapex25.com, www.tryapex25.com and apex25.theresume.io are pure marketing pages. Clerk's
  // production instance rejects tryapex25.com, and nobody signs in on these hosts, so they render
  // the Apex 25 page with no Clerk, no auth bootstrap and no app providers.
  root.render(
    <StrictMode>
      <RouterProvider router={createApex25MarketingRouter()} />
    </StrictMode>
  );
} else {
  root.render(
    <StrictMode>
      <ClerkProvider
        signInUrl="/sign-in"
        signUpUrl="/sign-up"
        signInForceRedirectUrl="/auth-callback"
        signUpForceRedirectUrl="/auth-callback"
        routerPush={(to) => router.navigate(to)}
        routerReplace={(to) => router.navigate(to, { replace: true })}
      >
        <QueryClientProvider client={queryClient}>
          <PaymentDialogProvider>
            {/* The Apex 25 header shows "Account" once signed in (tryapex25.com has no Clerk). */}
            <HeaderAuthSlot.Provider value={Apex25HeaderAuth}>
              <AppWithAuth />
            </HeaderAuthSlot.Provider>
          </PaymentDialogProvider>
          <Toaster />
        </QueryClientProvider>
      </ClerkProvider>
    </StrictMode>
  );
}
